import React, { useMemo } from 'react';
import { Box, Grid } from '@mui/material';
import styled from '@emotion/styled';
import { format, getDate, isToday as isTodayFn, parse } from 'date-fns';
import { useAppSelector } from '@/redux/hooks';
import { selectCurrentTableData } from '@/redux/selectors/dataTableSelectors';
import { CALENDAR_START_DATE_FORMAT, getMostRecentSunday, getUpcomingSaturday, parseStartDateQueryParam } from '@/_utils/date';
import { useQuery } from '@/components/_hooks/useQuery';
import { ApiHourlyData } from '@/components/_common/Table/Calendar/CalendarComponents/types';
import Cell from '@/components/_common/Table/Calendar/CalendarSharedComponents/Cell';
import CellHeader from '@/components/_common/Table/Calendar/CalendarSharedComponents/CellHeader';
import { getAvailStatus, getColorByAvailStatus, groupAvailsByDate } from '@/components/_common/Table/Calendar/utils';
import AvailDetailsPopover from '@/components/_common/Table/Calendar/CalendarComponents/AvailDetailsPopover';
import { Bullet, GridViewAvail, LicenseChip } from '@/components/_common/Table/Calendar/CalendarComponents/common';
import ShowMore from '@/components/_common/Table/Calendar/CalendarSharedComponents/ShowMore';
import { selectUserPreferences } from '@/redux/selectors/authSelectors';
import FocusImage from '@/components/Focus/common/FocusImage';
import { FOCUS_CALENDAR_WEEKLY_VIEW_ITEMS_PER_DAY } from '@/constants/constants';
import { useFocusTheme } from '@/components/Focus/configs/useFocusTheme.hook';

interface Props {
  calendarContainer: HTMLElement | null;
  totalCountByDay: Record<string, number> | undefined;
}

const WeeklyView: React.FC<Props> = ({ calendarContainer, totalCountByDay }) => {
  const theme = useFocusTheme();
  const query = useQuery();
  const calendarStartDateParam = query.get('calendarStartDate');
  const apiData: ApiHourlyData[] = useAppSelector(selectCurrentTableData);

  if (!calendarStartDateParam) return null;

  const itemsByDate = useMemo(() => {
    const parsedDate = parseStartDateQueryParam(calendarStartDateParam);
    const start = getMostRecentSunday(parsedDate);
    const end = getUpcomingSaturday(parsedDate);
    return groupAvailsByDate(start, end, apiData);
  }, [calendarStartDateParam, apiData]);

  return Object.entries(itemsByDate).map(([date, avails]) => {
    const dateParsed = parse(date, CALENDAR_START_DATE_FORMAT, new Date());
    const isToday = isTodayFn(dateParsed);
    const isFirstDayOfMonth = getDate(dateParsed) === 1;
    const dateLabel = isFirstDayOfMonth ? format(dateParsed, 'MMM d') : format(dateParsed, 'd');
    const { time_format } = useAppSelector(selectUserPreferences);

    return (
      <Cell key={date} isHighlighted={isToday}>
        <CellHeader label={dateLabel} isHighlighted={isFirstDayOfMonth} isEmphasized={isToday} />

        <ShowMore date={date} limit={FOCUS_CALENDAR_WEEKLY_VIEW_ITEMS_PER_DAY} totalCount={totalCountByDay?.[date]} avails={avails}>
          {(avail: ApiHourlyData) => {
            const time = format(new Date(avail.term_start), time_format || 'hh:mm aaaaa');
            const status = getAvailStatus(avail);
            const color = getColorByAvailStatus(status, theme.palette.mode === 'dark');

            return (
              <AvailDetailsPopover key={avail.id} availId={avail.id} calendarContainer={calendarContainer}>
                <GridViewAvail availId={avail.id} status={status}>
                  <Grid container gap={1} alignItems="center" sx={{ mb: 1 }}>
                    <Grid item>
                      <Bullet sx={{ backgroundColor: color }} />
                    </Grid>
                    <Grid item sx={{ flexGrow: 1, fontSize: '.857rem', lineHeight: '1em' }}>
                      {avail.title.client_ids_supplemental.alpha_id}
                    </Grid>
                    <Grid item sx={{ textTransform: 'uppercase', fontSize: '0.714rem', fontWeight: 700 }}>
                      {time}
                    </Grid>
                  </Grid>
                  <Grid container alignItems="end" gap={1}>
                    <Grid columnGap={1} item xs>
                      {avail.license_types.map((l) => (
                        <LicenseChip key={l} label={l.toLocaleUpperCase()} />
                      ))}
                    </Grid>
                    <Grid item>
                      {/* TODO: provide real thumbnail */}
                      <ThumbnailPlaceholder>
                        <FocusImage entity={avail} />
                      </ThumbnailPlaceholder>
                    </Grid>
                  </Grid>
                </GridViewAvail>
              </AvailDetailsPopover>
            );
          }}
        </ShowMore>
      </Cell>
    );
  });
};

export default WeeklyView;

const ThumbnailPlaceholder = styled(Box)`
  width: 80px;
  aspect-ratio: 16/9;
  border-radius: ${({ theme }) => `${theme.shape.borderRadius}px`};
  background-color: #4a4d5a;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7a7d8a;
`;
